import {
    DRIVER_LICENSE_TYPES,
    OPTIONAL_CONTACT_FIELD_OPTIONS,
    SINGLE_UPLOAD_ID_TYPES,
} from '@common/constants/contactFields';
import type { IntakeFormTranslations } from '@common/i18n/intakeForm';
import type { ContactFormValues } from '@common/types/formSubmissions';
import type {
    ContactBlockHiddenFields,
    OptionalCoreContactFields,
    QuestionSchema,
} from '@common/types/templateFormFields';
import type { RequiredContactFields, TemplateFormLanguage } from '@common/types/templateForms';
import {
    ensureContactBlockEntries,
    getContactBlockConfig,
    isContactBlockEntryBlank,
    parseContactBlockValue,
} from '@common/utils/contactBlock';
import { isValidEmail } from '@common/utils/isValidEmail';
import { getVisibleQuestions } from '@common/utils/questionVisibility';
import { isEmailField, validateFieldValue } from './fieldValidation';
import { isEmptyFormValue } from './types';

export type ContactFormErrors = Partial<Record<keyof ContactFormValues, string>>;

export interface ContactFieldConfig {
    hiddenFields?: ContactBlockHiddenFields;
    requiredFields?: RequiredContactFields;
    optionalCoreFields?: OptionalCoreContactFields;
}

export interface ContactValidationMessages {
    fieldRequired: string;
    invalidEmail: string;
}

export interface ContactEntryValidation {
    errors: ContactFormErrors;
    hasInvalidEmail: boolean;
}

function validateRequiredIdUploads(
    idType: string | undefined,
    contact: ContactFormValues,
    prefix: 'firstId' | 'secondId',
    errors: ContactFormErrors,
    fieldRequiredMessage: string,
): boolean {
    let isValid = true;

    if (DRIVER_LICENSE_TYPES.includes(idType ?? '')) {
        if (isEmptyFormValue(contact[`${prefix}Front`])) {
            errors[`${prefix}Front`] = fieldRequiredMessage;
            isValid = false;
        }
        if (isEmptyFormValue(contact[`${prefix}Back`])) {
            errors[`${prefix}Back`] = fieldRequiredMessage;
            isValid = false;
        }
    } else if (SINGLE_UPLOAD_ID_TYPES.includes(idType ?? '')) {
        const field = `${prefix}Single` as keyof ContactFormValues;
        if (isEmptyFormValue(contact[field])) {
            errors[field] = fieldRequiredMessage;
            isValid = false;
        }
    }

    return isValid;
}

/**
 * Validates one contact against a contact field config. Shared by the step-0 contact section
 * and contact block entries. Duplicate-email checks are the caller's responsibility.
 */
export function validateContactEntry(
    contact: ContactFormValues,
    config: ContactFieldConfig,
    messages: ContactValidationMessages,
): ContactEntryValidation {
    const { hiddenFields, requiredFields, optionalCoreFields } = config;
    const { fieldRequired, invalidEmail } = messages;
    const errors: ContactFormErrors = {};
    let hasInvalidEmail = false;

    const requiredCoreFields: (keyof ContactFormValues)[] = ['firstName', 'lastName'];
    if (!optionalCoreFields?.email && !hiddenFields?.email) requiredCoreFields.push('email');
    if (!optionalCoreFields?.cellPhone && !hiddenFields?.cellPhone) {
        requiredCoreFields.push('cellPhone');
    }

    for (const field of requiredCoreFields) {
        if (isEmptyFormValue(contact[field])) {
            errors[field] = fieldRequired;
        }
    }

    if (
        !errors.email &&
        !hiddenFields?.email &&
        !isEmptyFormValue(contact.email) &&
        !isValidEmail(contact.email)
    ) {
        errors.email = invalidEmail;
        hasInvalidEmail = true;
    }

    for (const field of OPTIONAL_CONTACT_FIELD_OPTIONS) {
        if (!requiredFields?.[field] || hiddenFields?.[field]) continue;

        if (field === 'firstId' || field === 'secondId') {
            const idTypeField = field === 'firstId' ? 'firstIdType' : 'secondIdType';
            if (isEmptyFormValue(contact[idTypeField])) {
                errors[idTypeField] = fieldRequired;
            } else {
                validateRequiredIdUploads(
                    contact[idTypeField],
                    contact,
                    field,
                    errors,
                    fieldRequired,
                );
            }
            continue;
        }

        if (isEmptyFormValue(contact[field])) {
            errors[field] = fieldRequired;
        }
    }

    return { errors, hasInvalidEmail };
}

export function hasContactFormErrors(errors?: ContactFormErrors): boolean {
    return Object.values(errors ?? {}).some(Boolean);
}

export interface ContactBlockEntryErrors {
    contact: ContactFormErrors;
    /** Nested question errors keyed by question name. */
    answers: Record<string, string | null>;
}

export interface ContactBlockValidation {
    entryErrors: ContactBlockEntryErrors[];
    isValid: boolean;
    hasInvalidEmail: boolean;
}

/**
 * Validates every entry of a `contactInfoSection` answer: each entry's contact fields and each
 * visible nested question (with the same rules as top-level questions). Blank entries are not part
 * of the answer and are skipped, except that a required block with nothing filled in validates its
 * first entry. A block with more entries than its limit is invalid.
 */
export function validateContactBlock(
    value: string | null | undefined,
    schema: QuestionSchema,
    isRequired: boolean,
    t: Pick<IntakeFormTranslations, 'contactInfo' | 'formValidation'>,
    language: TemplateFormLanguage,
): ContactBlockValidation {
    const config = getContactBlockConfig(schema);
    const entries = ensureContactBlockEntries(parseContactBlockValue(value).entries);
    const questions = config.questions ?? [];
    const isBlank = entries.map((entry) => isContactBlockEntryBlank(entry, questions));
    const hasFilledEntry = isBlank.includes(false);
    // The limit can be lowered after a client has already added entries
    let isValid = config.maxEntries === undefined || entries.length <= config.maxEntries;
    let hasInvalidEmail = false;

    const entryErrors = entries.map((entry, index) => {
        if (isBlank[index] && (!isRequired || hasFilledEntry || index > 0)) {
            return { contact: {}, answers: {} };
        }

        const contactResult = config.hideContactFields
            ? { errors: {}, hasInvalidEmail: false }
            : validateContactEntry(entry.contact, config, t.contactInfo.validation);
        hasInvalidEmail = hasInvalidEmail || contactResult.hasInvalidEmail;
        if (hasContactFormErrors(contactResult.errors)) isValid = false;

        const answers: Record<string, string | null> = {};
        const visibleQuestions = getVisibleQuestions(
            questions,
            (question) => entry.answers[question.questionSchema.name],
        );
        for (const question of visibleQuestions) {
            const questionName = question.questionSchema.name;
            const error = validateFieldValue(
                question,
                entry.answers[questionName] ?? question.questionSchema.value ?? '',
                t.formValidation,
                language,
            );
            if (error) {
                isValid = false;
                answers[questionName] = error;
                if (isEmailField(question)) {
                    hasInvalidEmail = hasInvalidEmail || error !== t.formValidation.fieldRequired;
                }
            }
        }

        return { contact: contactResult.errors, answers };
    });

    return { entryErrors, isValid, hasInvalidEmail };
}
